/**
 * Reusable Confirmation Modal Component
 */
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Trash2, RotateCcw, RefreshCw } from 'lucide-react';

const iconMap = {
  delete: Trash2,
  reset: RotateCcw,
  warning: AlertTriangle,
};

const colorMap = {
  delete: {
    bg: 'bg-destructive/10',
    icon: 'text-destructive',
    button: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  },
  reset: {
    bg: 'bg-orange-100',
    icon: 'text-orange-600',
    button: 'bg-orange-500 text-white hover:bg-orange-600',
  },
  warning: {
    bg: 'bg-yellow-100',
    icon: 'text-yellow-600',
    button: 'bg-yellow-500 text-white hover:bg-yellow-600',
  },
};

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  warning,
  type = 'delete',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
}) => {
  if (!isOpen) return null;

  const Icon = iconMap[type] || AlertTriangle;
  const colors = colorMap[type] || colorMap.warning;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="confirm-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-3xl p-8 max-w-md mx-4 shadow-2xl border border-border"
      >
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full ${colors.bg} flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${colors.icon}`} />
          </div>
          <h3 className="text-xl font-bold mb-2">{title}</h3>
          <p className="text-muted-foreground mb-4">{description}</p>
          {warning && (
            <p className={`text-sm ${colors.icon} font-medium`}>{warning}</p>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-6 py-3 font-medium transition-all disabled:opacity-50"
            data-testid="confirm-modal-cancel"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${colors.button} rounded-full px-6 py-3 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2`}
            data-testid="confirm-modal-confirm"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ConfirmModal;
